import { ContentState, convertFromHTML, convertFromRaw, convertToRaw, DraftBlockType, EditorState, RawDraftContentBlock, RawDraftEntity, RawDraftEntityRange, RawDraftInlineStyleRange } from "draft-js";
import { Options, stateToHTML } from "draft-js-export-html";
import { IdGen } from "@base/util/IdGen";
import { EditorRawState } from "./EditorDefinitions";

interface RawDraftContentBlockCompressed {
  k?: string;
  ty?: DraftBlockType;
  txt?: string;
  de?: number;
  isr?: RawDraftInlineStyleRange[];
  er?: RawDraftEntityRange[];
  da?: { [key: string]: unknown };
}

interface EditorRawStateCompressed {
  b: RawDraftContentBlockCompressed[];
  em?: { [key: string]: RawDraftEntity };
}

export class EditorModelHelper {
  static createEmptyRawState(): EditorRawState {
    return convertToRaw(EditorState.createEmpty().getCurrentContent());
  }

  static toEditorState(rawState: EditorRawState | undefined): EditorState {
    if (!rawState) rawState = this.createEmptyRawState();
    const contentState = convertFromRaw(rawState);
    return EditorState.createWithContent(contentState);
  }

  static toRawState(editorState: EditorState): EditorRawState {
    const rawState = convertToRaw(editorState.getCurrentContent());
    return rawState;
  }

  static getFirstLettersFromState(editorState: EditorState): string {
    return EditorRawStateHelper.getFirstLettersFromRawState(convertToRaw(editorState.getCurrentContent()));
  }
}

export class EditorRawStateHelper {
  private static compressBlock(block: RawDraftContentBlock, discardKeys?: boolean): RawDraftContentBlockCompressed {
    const { data, depth, entityRanges, inlineStyleRanges, key, text, type, ...rest } = block;
    const compressed: RawDraftContentBlockCompressed = { ...rest };

    if (Object.keys(data ? data : {}).length > 0) compressed.da = data;
    if (depth) compressed.de = block.depth;
    if (entityRanges.length) compressed.er = block.entityRanges;
    if (inlineStyleRanges.length) compressed.isr = block.inlineStyleRanges;
    if (key && !discardKeys) compressed.k = block.key;
    if (text) compressed.txt = block.text;
    if (type !== "unstyled") compressed.ty = block.type;
    return compressed;
  }

  private static expandBlock(block: RawDraftContentBlockCompressed): RawDraftContentBlock {
    const { da, de, er, isr, k, txt, ty, ...rest } = block;
    const expanded: RawDraftContentBlock = {
      ...rest,
      data: da ? da : {},
      depth: de ? de : 0,
      entityRanges: er ? er : [],
      inlineStyleRanges: isr ? isr : [],
      key: k ? k : IdGen.unsecure.get(),
      text: txt ? txt : "",
      type: ty ? ty : "unstyled",
    };
    return expanded;
  }

  static isCompressed(state: EditorRawState | EditorRawStateCompressed): state is EditorRawStateCompressed {
    return (state as EditorRawStateCompressed).b !== undefined;
  }

  static isEmpty(state: EditorRawState | EditorRawStateCompressed): boolean {
    if (this.isCompressed(state)) {
      return state.b.length === 1 && !state.b[0].txt;
    } else {
      return state.blocks.length === 1 && !state.blocks[0].text;
    }
  }

  static compress(state: EditorRawState): EditorRawStateCompressed {
    const { blocks, entityMap, ...rest } = state;
    const compact: EditorRawStateCompressed = {
      ...rest,
      b: blocks.map((block) => this.compressBlock(block)),
    };
    if (Object.keys(entityMap).length === 0) compact.em = entityMap;
    return compact;
  }

  static expand(state: EditorRawStateCompressed): EditorRawState {
    const { b, em, ...rest } = state;
    return {
      ...rest,
      blocks: b.map((block) => this.expandBlock(block)),
      entityMap: em ? em : {},
    };
  }

  static getFirstLettersFromRawState(state: EditorRawState | EditorRawStateCompressed | undefined, count?: number): string {
    if (!state) return "";
    if (count === undefined) count = 40;

    if (this.isCompressed(state)) state = this.expand(state);

    let text = "";
    for (const b of state.blocks) {
      if (text.length > 0) text += " ";
      text += b.text;
      if (text.length > count) {
        text = text.substring(0, count) + "...";
        return text;
      }
    }
    return text;
  }

  static rawStateToText(state: EditorRawState | EditorRawStateCompressed | undefined, maxLength?: number): string {
    if (!state) return "";

    if (this.isCompressed(state)) state = this.expand(state);

    const { blocks } = state;
    const mappedBlocks = blocks.map(
      block => (!block.text.trim() && "\n") || block.text,
    );

    let newText = "";
    for (let i = 0; i < mappedBlocks.length; i++) {
      const block = mappedBlocks[i];

      // handle last block
      if (i === mappedBlocks.length - 1) {
        newText += block;
      } else {
        // otherwise we join with \n, except if the block is already a \n
        if (block === "\n")
          newText += block;
        else
          newText += block + "\n";
      }

      if (maxLength !== undefined) {
        if (newText.length > maxLength) {
          newText = newText.substring(0, maxLength - 3) + "...";
        }
      }
    }
    return newText;
  }

  static rawToHtml(state: EditorRawState): string {
    const contentState = convertFromRaw(state);
    const html = stateToHTML(contentState, EditorRawStateHelper.getOptions());
    return html;
  }

  static htmlToRaw(html: string): EditorRawState | undefined {
    // remove \n => spaces would be added otherwise...
    const correctedHTML = this.removeNewLines(html);
    const blocksFromHtml = convertFromHTML(correctedHTML);
    const state = blocksFromHtml ? ContentState.createFromBlockArray(blocksFromHtml.contentBlocks, blocksFromHtml.entityMap) : undefined;
    const rawState = state ? convertToRaw(state) : undefined;
    return rawState;
  }

  static htmlToState(html: string): EditorState {
    const correctedHTML = this.removeNewLines(html);
    const blocksFromHtml = convertFromHTML(correctedHTML, undefined);
    const state = blocksFromHtml ? ContentState.createFromBlockArray(blocksFromHtml.contentBlocks, blocksFromHtml.entityMap) : undefined;
    return state ? EditorState.createWithContent(state) : EditorState.createEmpty();
  }

  static stateToHtml(state: EditorState): string {
    const html = stateToHTML(state.getCurrentContent(), EditorRawStateHelper.getOptions());
    return html;
  }

  static removeNewLines(text: string): string {
    return text.replace(/\n/g, "");
  }

  static getOptions(): Options {
    return {
      inlineStyles: {
        RED: { style: { color: "#ff0000" } },
        HIGHLIGHT: { style: { backgroundColor: "#ffff00" } }
      }
    };
  }
}
